import type { Attendance, ClassRoom, TimetableSlot } from '@/lib/types';
import { formatTime } from '@/lib/constants';

export type ClassInfoMaps = {
  classMap: Record<string, ClassRoom>;
  slotMap: Record<string, TimetableSlot>;
};

// 수업 ID + 요일 → 시간표 슬롯을 빠르게 찾을 수 있게 인덱스를 만들어요.
export function buildClassInfoMaps(
  classes: ClassRoom[],
  slots: TimetableSlot[]
): ClassInfoMaps {
  const classMap: Record<string, ClassRoom> = {};
  classes.forEach((c) => {
    classMap[c.id] = c;
  });

  const slotMap: Record<string, TimetableSlot> = {};
  slots.forEach((s) => {
    slotMap[slotKey(s.class_id, s.day_of_week)] = s;
  });

  return { classMap, slotMap };
}

function slotKey(classId: string, dayOfWeek: number): string {
  return `${classId}__${dayOfWeek}`;
}

// 결석한 날짜의 요일(0=일 ~ 6=토)을 구해요.
export function dayOfWeekOf(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

export type AttendanceClassInfo = {
  className: string | null;
  timeLabel: string | null;
};

// 결석 기록에 연결된 수업명과 그 요일의 수업 시간을 반환해요.
export function attendanceClassInfo(
  record: Attendance,
  maps: ClassInfoMaps
): AttendanceClassInfo {
  const cls = maps.classMap[record.class_id];
  const slot = maps.slotMap[slotKey(record.class_id, dayOfWeekOf(record.date))];
  const timeLabel = slot
    ? `${formatTime(slot.start_time)}~${formatTime(slot.end_time)}`
    : null;
  return {
    className: cls?.name || cls?.subject || null,
    timeLabel,
  };
}

// 결석 사유를 사람이 읽기 좋은 문장으로 만들어요.
export function absenceReasonLabel(record: Attendance): string {
  const reason = record.absence_type || '결석';
  if (record.absence_type === '기타' && record.note) return `${reason}(${record.note})`;
  return reason;
}

// 목록에서 쓰는 "수업명 · 시간" 한 줄 문구.
export function classInfoLine(info: AttendanceClassInfo): string {
  const parts: string[] = [];
  if (info.className) parts.push(info.className);
  if (info.timeLabel) parts.push(info.timeLabel);
  return parts.join(' · ');
}